/**
 * Metadata Standards Compliance Checker
 * Validates that all data entities follow Magnus Core Metadata Standards v2.0.0
 * 
 * See: docs/METADATA_STANDARDS.md
 */

import { DfrData } from '../types';

export interface ComplianceIssue {
    severity: 'error' | 'warning';
    category: 'projectMetadata' | 'iterationHistory' | 'dataEntity' | 'fileNaming';
    field: string;
    message: string;
    location?: string;
}

export interface ComplianceReport {
    isCompliant: boolean;
    score: number; // 0-100
    issues: ComplianceIssue[];
    summary: {
        totalChecks: number;
        passed: number;
        failed: number;
        warnings: number;
    };
}

/**
 * Check if project metadata is compliant with standards
 */
export function validateProjectMetadata(data: DfrData): ComplianceIssue[] {
    const issues: ComplianceIssue[] = [];
    const metadata = data.projectMetadata;

    if (!metadata) {
        issues.push({
            severity: 'error',
            category: 'projectMetadata',
            field: 'projectMetadata',
            message: 'Missing required projectMetadata object'
        });
        return issues;
    }

    // Required fields
    const requiredFields = ['id', 'name', 'description', 'created', 'lastModified', 'version', 'schemaVersion', 'author'];

    requiredFields.forEach(field => {
        if (!metadata[field]) {
            issues.push({
                severity: 'error',
                category: 'projectMetadata',
                field,
                message: `Missing required field: projectMetadata.${field}`
            });
        }
    });

    // Schema version check
    if (metadata.schemaVersion && metadata.schemaVersion !== '2.0.0') {
        issues.push({
            severity: 'error',
            category: 'projectMetadata',
            field: 'schemaVersion',
            message: `Invalid schemaVersion "${metadata.schemaVersion}". Must be "2.0.0"`
        });
    }

    // Version format check (semantic versioning)
    if (metadata.version && !/^\d+\.\d+\.\d+$/.test(metadata.version)) {
        issues.push({
            severity: 'warning',
            category: 'projectMetadata',
            field: 'version',
            message: `Version "${metadata.version}" should follow semantic versioning (e.g., "1.0.0")`
        });
    }

    // ISO 8601 timestamp check
    if (metadata.created && isNaN(Date.parse(metadata.created))) {
        issues.push({
            severity: 'error',
            category: 'projectMetadata',
            field: 'created',
            message: `Invalid ISO 8601 timestamp for created: "${metadata.created}"`
        });
    }

    if (metadata.lastModified && isNaN(Date.parse(metadata.lastModified))) {
        issues.push({
            severity: 'error',
            category: 'projectMetadata',
            field: 'lastModified',
            message: `Invalid ISO 8601 timestamp for lastModified: "${metadata.lastModified}"`
        });
    }

    return issues;
}

/**
 * Check if iteration history is compliant
 */
export function validateIterationHistory(data: DfrData): ComplianceIssue[] {
    const issues: ComplianceIssue[] = [];
    const history = data.iterationHistory;

    if (!history || !Array.isArray(history)) {
        issues.push({
            severity: 'warning',
            category: 'iterationHistory',
            field: 'iterationHistory',
            message: 'Missing iterationHistory array'
        });
        return issues;
    }

    history.forEach((epoch, index) => {
        // Required fields
        if (typeof epoch.epochNumber !== 'number') {
            issues.push({
                severity: 'error',
                category: 'iterationHistory',
                field: `iterationHistory[${index}].epochNumber`,
                message: `Missing or invalid epochNumber at index ${index}`
            });
        }

        if (!epoch.epochName) {
            issues.push({
                severity: 'error',
                category: 'iterationHistory',
                field: `iterationHistory[${index}].epochName`,
                message: `Missing epochName at index ${index}`
            });
        }

        if (!epoch.projectName) {
            issues.push({
                severity: 'error',
                category: 'iterationHistory',
                field: `iterationHistory[${index}].projectName`,
                message: `Missing denormalized projectName at index ${index}`
            });
        }

        // Format check for epochName
        if (epoch.epochName && !epoch.epochName.includes('Epoch')) {
            issues.push({
                severity: 'warning',
                category: 'iterationHistory',
                field: `iterationHistory[${index}].epochName`,
                message: `epochName "${epoch.epochName}" should follow format "{Project Name} - Epoch {N}"`
            });
        }

        // projectName should match projectMetadata.name
        if (epoch.projectName && data.projectMetadata?.name && epoch.projectName !== data.projectMetadata.name) {
            issues.push({
                severity: 'warning',
                category: 'iterationHistory',
                field: `iterationHistory[${index}].projectName`,
                message: `projectName "${epoch.projectName}" doesn't match projectMetadata.name "${data.projectMetadata.name}"`
            });
        }
    });

    return issues;
}

/**
 * Check if data entities (FMEA, BOM, etc.) are compliant
 */
export function validateDataEntities(data: DfrData): ComplianceIssue[] {
    const issues: ComplianceIssue[] = [];

    // Check FMEA items
    if (data.fmeaItems && Array.isArray(data.fmeaItems)) {
        data.fmeaItems.forEach((item: any, index) => {
            if (!item.projectName) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `fmeaItems[${index}].projectName`,
                    message: `Missing required projectName for FMEA item "${item.failureMode || 'unknown'}"`,
                    location: `fmeaItems[${index}]`
                });
            }

            if (item.epochId === undefined || item.epochId === null) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `fmeaItems[${index}].epochId`,
                    message: `Missing required epochId for FMEA item "${item.failureMode || 'unknown'}"`,
                    location: `fmeaItems[${index}]`
                });
            }

            // Validate status field
            if (!item.status) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `fmeaItems[${index}].status`,
                    message: `Missing required status for FMEA item "${item.failureMode || 'unknown'}" (should be 'draft', 'active', 'archived', or 'superseded')`,
                    location: `fmeaItems[${index}]`
                });
            } else if (!['draft', 'active', 'archived', 'superseded'].includes(item.status)) {
                issues.push({
                    severity: 'warning',
                    category: 'dataEntity',
                    field: `fmeaItems[${index}].status`,
                    message: `Invalid status "${item.status}" for FMEA item. Allowed: 'draft', 'active', 'archived', 'superseded'`,
                    location: `fmeaItems[${index}]`
                });
            }

            // projectName should match projectMetadata.name
            if (item.projectName && data.projectMetadata?.name &&
                item.projectName !== data.projectMetadata.name) {
                issues.push({
                    severity: 'warning',
                    category: 'dataEntity',
                    field: `fmeaItems[${index}].projectName`,
                    message: `projectName "${item.projectName}" doesn't match projectMetadata.name "${data.projectMetadata.name}"`,
                    location: `fmeaItems[${index}]`
                });
            }
        });
    }

    // Check BOM items
    if (data.bomItems && Array.isArray(data.bomItems)) {
        data.bomItems.forEach((item: any, index) => {
            if (!item.projectName) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `bomItems[${index}].projectName`,
                    message: `Missing required projectName for BOM item "${item.component || 'unknown'}"`,
                    location: `bomItems[${index}]`
                });
            }

            if (item.epochId === undefined || item.epochId === null) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `bomItems[${index}].epochId`,
                    message: `Missing required epochId for BOM item "${item.component || 'unknown'}"`,
                    location: `bomItems[${index}]`
                });
            }
        });
    }

    // Check DVP&R tests
    if (data.dvprTests && Array.isArray(data.dvprTests)) {
        data.dvprTests.forEach((item: any, index) => {
            if (!item.projectName) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `dvprTests[${index}].projectName`,
                    message: `Missing required projectName for DVP&R test "${item.testName || 'unknown'}"`,
                    location: `dvprTests[${index}]`
                });
            }

            if (item.epochId === undefined || item.epochId === null) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `dvprTests[${index}].epochId`,
                    message: `Missing required epochId for DVP&R test "${item.testName || 'unknown'}"`,
                    location: `dvprTests[${index}]`
                });
            }
        });
    }

    // Check P-Diagrams
    if (data.pDiagramList && Array.isArray(data.pDiagramList)) {
        data.pDiagramList.forEach((item: any, index) => {
            if (!item.projectName) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `pDiagramList[${index}].projectName`,
                    message: `Missing required projectName for P-Diagram "${item.function || 'unknown'}"`,
                    location: `pDiagramList[${index}]`
                });
            }

            if (item.epochId === undefined || item.epochId === null) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `pDiagramList[${index}].epochId`,
                    message: `Missing required epochId for P-Diagram "${item.function || 'unknown'}"`,
                    location: `pDiagramList[${index}]`
                });
            }

            // Check for required status field (Metadata Standards v2.0.0)
            if (!item.status) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `pDiagramList[${index}].status`,
                    message: `Missing REQUIRED 'status' field for P-Diagram "${item.consolidatedFunctionId || 'unknown'}". Must be 'active' or 'draft'`,
                    location: `pDiagramList[${index}]`
                });
            }
        });
    }

    return issues;
}

/**
 * Check if PBS revision data is compliant
 */
export function validatePbsRevisions(data: DfrData): ComplianceIssue[] {
    const issues: ComplianceIssue[] = [];

    // Check PBS revisions
    if (data.pbsRevisions && Array.isArray(data.pbsRevisions)) {
        data.pbsRevisions.forEach((revision: any, index) => {
            if (!revision.projectName) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `pbsRevisions[${index}].projectName`,
                    message: `Missing required projectName for PBS revision "${revision.name || 'unknown'}"`,
                    location: `pbsRevisions[${index}]`
                });
            }

            if (!revision.stage) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `pbsRevisions[${index}].stage`,
                    message: `Missing required stage for PBS revision "${revision.name || 'unknown'}"`,
                    location: `pbsRevisions[${index}]`
                });
            }

            // Validate only one active revision per project
            const activeRevisions = data.pbsRevisions?.filter((r: any) => r.isActive) || [];
            if (activeRevisions.length > 1) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: 'pbsRevisions',
                    message: `Multiple active PBS revisions found. Only one can be active per project.`,
                    location: 'pbsRevisions'
                });
            }
        });
    }

    // Check PBS items
    if (data.pbsItems && Array.isArray(data.pbsItems)) {
        data.pbsItems.forEach((item: any, index) => {
            if (!item.projectName) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `pbsItems[${index}].projectName`,
                    message: `Missing required projectName for PBS item "${item.name || 'unknown'}"`,
                    location: `pbsItems[${index}]`
                });
            }

            if (item.epochId === undefined || item.epochId === null) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `pbsItems[${index}].epochId`,
                    message: `Missing required epochId for PBS item "${item.name || 'unknown'}"`,
                    location: `pbsItems[${index}]`
                });
            }

            if (!item.revisionLevel) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `pbsItems[${index}].revisionLevel`,
                    message: `Missing required revisionLevel for PBS item "${item.name || 'unknown'}"`,
                    location: `pbsItems[${index}]`
                });
            }

            // Validate revisionLevel references a valid revision
            if (item.revisionLevel && data.pbsRevisions) {
                const revisionExists = data.pbsRevisions.some((r: any) => r.id === item.revisionLevel);
                if (!revisionExists) {
                    issues.push({
                        severity: 'error',
                        category: 'dataEntity',
                        field: `pbsItems[${index}].revisionLevel`,
                        message: `PBS item references non-existent revision "${item.revisionLevel}"`,
                        location: `pbsItems[${index}]`
                    });
                }
            }

            // Validate review status for carried-over items
            if (item.carriedOverFrom && !item.reviewStatus) {
                issues.push({
                    severity: 'warning',
                    category: 'dataEntity',
                    field: `pbsItems[${index}].reviewStatus`,
                    message: `Carried-over PBS item missing reviewStatus field`,
                    location: `pbsItems[${index}]`
                });
            }
        });
    }

    return issues;
}

/**
 * Check if ECN (Engineering Change Notice) data is compliant
 */
export function validateEcnLog(data: DfrData): ComplianceIssue[] {
    const issues: ComplianceIssue[] = [];

    if (data.ecnLog && Array.isArray(data.ecnLog)) {
        data.ecnLog.forEach((ecn: any, index) => {
            // Required: Project Name
            if (!ecn.projectName) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `ecnLog[${index}].projectName`,
                    message: `Missing required projectName for ECN "${ecn.ecnNumber || 'unknown'}"`,
                    location: `ecnLog[${index}]`
                });
            }

            // Required: Epoch ID
            if (ecn.epochId === undefined || ecn.epochId === null) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `ecnLog[${index}].epochId`,
                    message: `Missing required epochId for ECN "${ecn.ecnNumber || 'unknown'}"`,
                    location: `ecnLog[${index}]`
                });
            }

            // Required: Status
            if (!ecn.status) {
                issues.push({
                    severity: 'error',
                    category: 'dataEntity',
                    field: `ecnLog[${index}].status`,
                    message: `Missing required status for ECN "${ecn.ecnNumber || 'unknown'}"`,
                    location: `ecnLog[${index}]`
                });
            }

            // Validate PBS integration if present
            if (ecn.pbsRevisionId && data.pbsRevisions) {
                const revisionExists = data.pbsRevisions.some((r: any) => r.id === ecn.pbsRevisionId);
                if (!revisionExists) {
                    issues.push({
                        severity: 'error',
                        category: 'dataEntity',
                        field: `ecnLog[${index}].pbsRevisionId`,
                        message: `ECN "${ecn.ecnNumber}" references non-existent PBS revision`,
                        location: `ecnLog[${index}]`
                    });
                }
            }
        });
    }

    return issues;
}

/**
 * Run a complete compliance check on project data
 */
export function checkCompliance(data: DfrData): ComplianceReport {
    const issues: ComplianceIssue[] = []

    // Run all validation checks
    issues.push(...validateProjectMetadata(data));
    issues.push(...validateIterationHistory(data));
    issues.push(...validateDataEntities(data));
    issues.push(...validatePbsRevisions(data));  // PBS validation
    issues.push(...validateEcnLog(data));        // ECN validation

    // Calculate summary
    const errors = issues.filter(i => i.severity === 'error');
    const warnings = issues.filter(i => i.severity === 'warning');
    const totalChecks = issues.length + 10; // Approximate base checks
    const passed = totalChecks - issues.length;
    const score = Math.round((passed / totalChecks) * 100);

    return {
        isCompliant: errors.length === 0,
        score,
        issues,
        summary: {
            totalChecks,
            passed,
            failed: errors.length,
            warnings: warnings.length
        }
    };
}

/**
 * Generate a human-readable compliance report
 */
export function generateComplianceReport(report: ComplianceReport): string {
    let output = '# Metadata Standards Compliance Report\\n\\n';

    output += `**Overall Compliance:** ${report.isCompliant ? '✅ PASS' : '❌ FAIL'}\\n`;
    output += `**Score:** ${report.score}/100\\n\\n`;

    output += `## Summary\\n`;
    output += `- Total Checks: ${report.summary.totalChecks}\\n`;
    output += `- Passed: ${report.summary.passed}\\n`;
    output += `- Failed: ${report.summary.failed}\\n`;
    output += `- Warnings: ${report.summary.warnings}\\n\\n`;

    if (report.issues.length === 0) {
        output += '✅ No issues found! This project is fully compliant with Metadata Standards v2.0.0\\n';
        return output;
    }

    output += `## Issues (${report.issues.length})\\n\\n`;

    // Group issues by category
    const grouped = report.issues.reduce((acc, issue) => {
        if (!acc[issue.category]) acc[issue.category] = [];
        acc[issue.category].push(issue);
        return acc;
    }, {} as Record<string, ComplianceIssue[]>);

    Object.entries(grouped).forEach(([category, categoryIssues]) => {
        output += `### ${category}\\n\\n`;
        categoryIssues.forEach(issue => {
            const icon = issue.severity === 'error' ? '❌' : '⚠️';
            output += `${icon} **${issue.field}**: ${issue.message}\\n`;
            if (issue.location) {
                output += `   _Location: ${issue.location}_\\n`;
            }
        });
        output += '\\n';
    });

    return output;
}

/**
 * Quick check if an individual entity is compliant
 */
export function isEntityCompliant(entity: any): boolean {
    if (!entity) return false;

    const hasProjectName = typeof entity.projectName === 'string' && entity.projectName.length > 0;
    const hasEpochId = typeof entity.epochId === 'number';

    return hasProjectName && hasEpochId;
}

/**
 * Add compliant metadata to an entity
 */
export function addMetadataToEntity<T extends Record<string, any>>(
    entity: T,
    projectName: string,
    epochId: number
): T & { projectName: string; epochId: number } {
    return {
        ...entity,
        projectName,
        epochId
    };
}
